import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { liveApi, type LiveLesson } from "../../lib/live-api";
import { getMe } from "../../lib/api";

function StatusBadge({ status }: { status: string }): JSX.Element {
  const color =
    status === "SCHEDULED" ? "bg-blue-500/30 text-blue-200" :
    status === "IN_PROGRESS" ? "bg-lime-500/30 text-lime-200 animate-pulse" :
    status === "COMPLETED" ? "bg-ink-700 text-ink-300" :
    "bg-red-500/30 text-red-200";
  return <span className={`px-2 py-0.5 rounded text-xs uppercase tracking-wide ${color}`}>{status}</span>;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
}

export function LiveDashboard(): JSX.Element {
  const [lessons, setLessons] = useState<LiveLesson[] | null>(null);
  const [, setMyRole] = useState<string | null>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setErr(null);
    try {
      const me = await getMe();
      // Role is added later from a separate request — for now we'll infer from lessons
      setMyId(me.user?.id ?? null);
      const { lessons } = await liveApi.myLessons();
      setLessons(lessons);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => { void load(); }, []);

  async function pickRole(role: "TUTOR" | "CLIENT"): Promise<void> {
    setBusy(true);
    try {
      const r = await liveApi.setRole(role);
      setMyRole(r.role);
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (err) {
    return (
      <div className="p-8 text-red-300">
        <div className="mb-3">Error: {err}</div>
        <Link to="/" className="underline">Wróć do AI tutora</Link>
      </div>
    );
  }
  if (lessons === null) {
    return <div className="p-8 text-ink-400">Ładowanie…</div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto text-ink-200">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-light tracking-tight">Sesje live</h1>
        <Link to="/" className="text-sm text-ink-400 hover:text-ink-200 underline">
          ← AI tutor
        </Link>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-3 max-w-md">
        <button
          disabled={busy}
          onClick={() => void pickRole("TUTOR")}
          className="rounded-xl border border-ink-700 hover:border-lime-500 bg-ink-800/40 px-4 py-3 text-left disabled:opacity-50"
        >
          <div className="text-sm uppercase tracking-widest text-ink-400">Jestem</div>
          <div className="text-lg font-medium">Tutorem</div>
          <div className="text-xs text-ink-400 mt-1">Prowadzę live sesje z klientami</div>
        </button>
        <button
          disabled={busy}
          onClick={() => void pickRole("CLIENT")}
          className="rounded-xl border border-ink-700 hover:border-lime-500 bg-ink-800/40 px-4 py-3 text-left disabled:opacity-50"
        >
          <div className="text-sm uppercase tracking-widest text-ink-400">Jestem</div>
          <div className="text-lg font-medium">Klientem</div>
          <div className="text-xs text-ink-400 mt-1">Rezerwuję sesje u tutorów</div>
        </button>
      </div>

      <div className="flex gap-3 mb-6">
        <Link to="/live/book" className="px-4 py-2 rounded-lg bg-lime-500/90 hover:bg-lime-400 text-ink-900 font-medium">
          Znajdź tutora
        </Link>
        <Link to="/live/availability" className="px-4 py-2 rounded-lg border border-ink-700 hover:border-ink-500">
          Moja dostępność (tutor)
        </Link>
      </div>

      <h2 className="text-lg mt-8 mb-3 text-ink-300">Moje lekcje</h2>
      {lessons.length === 0 ? (
        <div className="text-ink-500 text-sm">Brak lekcji. Wybierz rolę i znajdź tutora powyżej.</div>
      ) : (
        <ul className="space-y-2">
          {lessons.map((l) => {
            const isTutor = myId === l.tutor_id;
            const other = isTutor ? l.client : l.tutor;
            return (
              <li key={l.id} className="border border-ink-700 rounded-lg p-3 flex items-center justify-between bg-ink-800/40">
                <div>
                  <div className="text-sm text-ink-400">
                    {isTutor ? "Klient" : "Tutor"}: <span className="text-ink-200">{other?.name ?? other?.email ?? "—"}</span>
                  </div>
                  <div className="text-sm">
                    {fmtDate(l.scheduled_at)} · {l.duration_min} min
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={l.status} />
                  {(l.status === "SCHEDULED" || l.status === "IN_PROGRESS") && (
                    <Link
                      to={`/live/lesson/${l.id}`}
                      className="px-3 py-1.5 rounded bg-lime-500/90 hover:bg-lime-400 text-ink-900 text-sm font-medium"
                    >
                      Wejdź
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
